import { ReactElement, useEffect, useRef, useState } from 'react'

import { formatCurrency, formatNumber } from 'utils/formatters'
import { NetworkLabel } from 'components/ui/network-label'

import SearchIcon from 'images/search-icon.png'

import { ICurrency } from 'store/fetchers/currencies'

import ReactClickOutside from '@coxy/react-click-outside'

import cn from 'classnames'

import { useTranslation } from 'utils/use-translation'

import { floorNumber } from '../../../../../utils/floor-number'

import IconCheck from './icon-check.svg'
import styles from './styles.module.scss'

export const CoinList = ({
  list,
  selected,
  onChoose,
  showBalance = false,
  isStableFirst = false,
  listFullWidth = false,
  isExchangeLayout = false,
  isVip = false,
  isVisibleList = false,
  className = '',
  onClose = null,
  ref = null,
  type = 'primary'
}) => {
  const { t } = useTranslation()

  const handleChoose = (item: ICurrency) => () => {
    onChoose(item)
  }

  const [items, setItems] = useState<ReactElement[]>([])
  const [stableItems, setStableItems] = useState<ReactElement[]>([])
  const [searchValue, setSearchValue] = useState('')
  const inputRef = useRef(null);

  const handleDivClick = () => {
    inputRef.current?.focus()
  }

  const getCoinItem = (currency): ReactElement => {
    const code = currency.code
    const isSelected = code === selected?.code && currency.network === selected?.network

    return (
      <div
        className={cn(styles.coinsListItem, { [styles.vip]: isVip },
          { [styles['secondary__list-item']]: type === 'secondary' },
        )}
        key={`${code}-${currency.network}`}
        onClick={handleChoose(currency)}
        ref={ref}
      >
        {type !== 'secondary'
          ? <>
            <img alt={code} width={20} height={20} src={currency.logo_url} />
            <div className={styles.ticker}>{formatCurrency(currency, true)}</div>
            <NetworkLabel currency={currency} truncate={true} />
            <div className={styles.coinName}>
              {showBalance && currency?.balance && floorNumber(+Number(currency?.balance), 6) > 0
                ? formatNumber(floorNumber(+Number(currency?.balance), 6))
                : currency.name}
            </div>
            {isSelected && <img src={IconCheck} alt="CheckedCoin" className={styles.checkIcon} />}
          </>
          : <>
            <img alt={code} width={28} height={28} src={currency.logo_url} />
            <div className={styles.code}>
              <div>
                <div className={styles.ticker}>{formatCurrency(currency, true)}</div>
                <NetworkLabel currency={currency} truncate={true} type={type}/>
              </div>
              <div className={styles.coinName}>
                {showBalance && currency?.balance && floorNumber(+Number(currency?.balance), 6) > 0
                  ? formatNumber(floorNumber(+Number(currency?.balance), 6))
                  : currency.name}
              </div>
            </div>

            {isSelected && <img src={IconCheck} alt="CheckedCoin" className={styles.checkIcon} />}
          </>}
      </div>
    )
  }

  useEffect(() => {
    const exactMatches: ReactElement[] = []
    const partialMatches: ReactElement[] = []
    const exactStableMatches: ReactElement[] = []
    const partialStableMatches: ReactElement[] = []

    for (const currency of list) {
      const code = currency.code.toLowerCase()
      const search = searchValue.toLowerCase()

      if (code === search) {
        const item = getCoinItem(currency)

        currency.is_stable
          ? exactStableMatches.push(item)
          : exactMatches.push(item)
      } else if (code.includes(search)) {
        const item = getCoinItem(currency)

        currency.is_stable
          ? partialStableMatches.push(item)
          : partialMatches.push(item)
      }
    }

    setItems([...exactMatches, ...partialMatches])
    setStableItems([...exactStableMatches, ...partialStableMatches])
  }, [searchValue, list, showBalance, selected])

  function handleInput(e) {
    e.stopPropagation()
    setSearchValue(e.target.value.toLowerCase())
  }

  useEffect(() => {
    if (!isVisibleList) {
      setSearchValue('')
    }
  }, [isVisibleList])

  return (
    <ReactClickOutside onClose={onClose} visible={isVisibleList}>
      <div
        className={cn(
          styles.coinsList,
          { [styles.coinsList_exchange]: isExchangeLayout },
          { [styles.vip]: isVip },
          { [styles.coinsList_opened]: isVisibleList },
          { [styles.secondary]: type === 'secondary' },
          className,
        )}
        style={{ maxWidth: listFullWidth ? 'unset' : '310px' }}
      >
        <div onClick={handleDivClick} className={cn(styles.search,
          { [styles.vip]: isVip },
          { [styles.secondary__input]: type === 'secondary' },
        )}>
          <img src={SearchIcon} className={styles.searchIcon} alt="IconInputArrow" width={16} height={16} />
          <input
            ref={inputRef}
            onInput={handleInput}
            className={styles.searchInput}
            type="text"
            placeholder={t('search_crypto')} />
        </div>
        <div className={cn(styles.coinsListScrollbar, { [styles.vip]: isVip },
          { [styles.secondary__list]: type === 'secondary' },
        )}>
          {isStableFirst && stableItems.length > 0 && (
            <>
              <p className={cn(styles.searchGroup, { [styles.vip]: isVip })}>{t('stablecoins')}</p>
              {stableItems}
            </>
          )}

          {items.length > 0 && (
            <>
              <p className={cn(styles.searchGroup, { [styles.vip]: isVip })}>{t('crypto_assets')}</p>
              {items}
            </>
          )}

          {!isStableFirst && stableItems.length > 0 && (
            <>
              <p className={cn(styles.searchGroup, { [styles.vip]: isVip })}>{t('stablecoins')}</p>
              {stableItems}
            </>
          )}
        </div>
      </div>
    </ReactClickOutside>
  )
}
